import { useState, useEffect } from 'react'
import Action from '@/gf/components/Action'
import Modal from '@/gf/components/ModalNext'
import Spinner from '@/gf/components/Spinner'
import { ModalSize } from '@/types'
import UploadPreview from '@/gf/modules/UploadPreview'
import { FormItem } from '../types'
import useQuoteUpload from './useQuoteUpload'

// Line item shape from pending correction result
type CorrectedLineItem = {
  part_number: string
  description: string
  quantity: number
  unit_price: number | null
}

// Normalize price from extraction result (cents) to dollars for display
const normalizePrice = (price: unknown): number | null => {
  if (price == null) return null

  // Handle Money-like objects (from LlamaQuote)
  if (typeof price === 'object' && 'amount' in price) {
    const amount = (price as { amount: number }).amount
    return typeof amount === 'number' ? amount / 100 : null
  }

  // Handle integer cents (from AiAssistant)
  if (typeof price === 'number') {
    return price / 100
  }

  return null
}

const QuoteAssistantModal = ({
  open,
  onClose,
  attachmentUrls,
  currentLineItems,
}: {
  open: boolean
  onClose: () => void
  attachmentUrls: string[]
  currentLineItems: FormItem[]
}) => {
  const [prompt, setPrompt] = useState('')
  const {
    correctionLoading,
    correctionError,
    pendingCorrection,
    onRequestCorrection,
    onAcceptCorrection,
    onRejectCorrection,
    documentId,
  } = useQuoteUpload()

  // Reset prompt when modal closes
  useEffect(() => {
    if (!open) {
      setPrompt('')
    }
  }, [open])

  const handleSubmit = async () => {
    if (!prompt.trim()) return

    // Build a prompt that includes the current form line items context
    const existingContext =
      currentLineItems.length > 0
        ? `Here are the current line items in the quote form:\n\n${currentLineItems
            .map(
              (li, idx) =>
                `${idx + 1}. ${li.partNumber} - ${li.description} (qty: ${li.quantity})${li.unitPrice ? ` @ $${li.unitPrice.toFixed(2)}` : ''}`
            )
            .join('\n')}\n\n---\n\n`
        : ''

    const fullPrompt = existingContext + prompt

    try {
      await onRequestCorrection(fullPrompt)
    } catch (error) {
      // Error is already handled by the hook and exposed via correctionError
      console.error('Correction request failed:', error)
    }
  }

  // Format price - handles numbers (from form items and AI response)
  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-'
    return `$${price.toFixed(2)}`
  }

  const calculateTotal = (items: { quantity: number; unit_price: number | null }[]) => {
    return items.reduce((sum, item) => {
      if (item.unit_price === null) return sum
      return sum + item.unit_price * item.quantity
    }, 0)
  }

  const calculateFormTotal = (items: FormItem[]) => {
    return items.reduce((sum, item) => {
      if (item.unitPrice === null) return sum
      return sum + item.unitPrice * item.quantity
    }, 0)
  }

  // Type guard for line item validation
  const isValidLineItem = (
    li: unknown
  ): li is {
    part_number?: string
    description?: string
    quantity?: number
    unit_price?: unknown
  } => {
    if (li === null || typeof li !== 'object') return false
    const item = li as Record<string, unknown>
    // At minimum, we need something to identify the item
    const hasPartNumber = typeof item.part_number === 'string' || item.part_number === undefined
    const hasDescription = typeof item.description === 'string' || item.description === undefined
    const hasQuantity = typeof item.quantity === 'number' || item.quantity === undefined
    return hasPartNumber && hasDescription && hasQuantity
  }

  // Get line items from pending correction
  const getCorrectedLineItems = (): CorrectedLineItem[] => {
    if (!pendingCorrection?.result) return []

    const result = pendingCorrection.result as { line_items?: unknown[] }
    const lineItems = result.line_items

    if (!Array.isArray(lineItems)) return []

    return lineItems.filter(isValidLineItem).map((item) => ({
      part_number: item.part_number || '',
      description: item.description || '',
      quantity: typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1,
      unit_price: normalizePrice(item.unit_price),
    }))
  }

  const correctedLineItems = getCorrectedLineItems()

  // Get explanation from pending correction
  const getExplanation = (): string | null => {
    if (!pendingCorrection?.result) return null

    const result = pendingCorrection.result as { explanation?: string }
    const explanation = result.explanation

    return typeof explanation === 'string' && explanation.trim() ? explanation.trim() : null
  }

  const explanation = getExplanation()

  const handleApplyAndContinue = async () => {
    if (correctedLineItems.length === 0) return
    await onAcceptCorrection()
    // Clear for next iteration
    setPrompt('')
  }

  const handleApplyAndClose = async () => {
    if (correctedLineItems.length === 0) return
    await onAcceptCorrection()
    onClose()
  }

  const handleClose = () => {
    if (pendingCorrection) {
      onRejectCorrection()
    }
    onClose()
  }

  // Get the PDF URL for the link (prefer from attachmentUrls prop)
  // A quote uploaded this session is not stored yet: link its preview, as the raw upload
  // URL answers 403. A URL the server returned passes through unchanged.
  const pdfUrl = attachmentUrls[0] ? UploadPreview.src(attachmentUrls[0]) : null

  // Check if we can make corrections (need a document)
  const canRequestCorrection = !!documentId

  return (
    <Modal open={open} onClose={handleClose} size={ModalSize.XL}>
      <div className="flex flex-col h-full max-h-[90vh]">
        <div className="px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">Quote Assistant</h2>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-sm text-gray-600">
              Describe what needs to change and we will correct the quote.
            </p>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
              >
                View uploaded quote ↗
              </a>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row">
          {/* Left Column */}
          <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r flex flex-col md:overflow-hidden">
            {/* Current Quote table */}
            <div className="p-4">
              <div className="px-4 py-2 bg-gray-50 rounded-t-lg">
                <h3 className="font-medium text-sm">Current Quote</h3>
              </div>
              <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                <div className="max-h-48 md:max-h-80 overflow-auto">
                  {currentLineItems.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 border-b">
                        <tr>
                          <th className="text-left px-4 py-2 w-8">#</th>
                          <th className="text-left px-4 py-2">Part #</th>
                          <th className="text-left px-4 py-2">Description</th>
                          <th className="text-right px-4 py-2">Qty</th>
                          <th className="text-right px-4 py-2">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentLineItems.map((li, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                            <td className="px-4 py-2 font-mono text-xs">{li.partNumber}</td>
                            <td className="px-4 py-2 truncate max-w-[200px]">{li.description}</td>
                            <td className="px-4 py-2 text-right">{li.quantity}</td>
                            <td className="px-4 py-2 text-right">{formatPrice(li.unitPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-4 text-center text-gray-400 text-sm">
                      No line items extracted yet
                    </div>
                  )}
                  {currentLineItems.length > 0 && (
                    <div className="sticky bottom-0 bg-gray-100 border-t-2">
                      <div className="flex justify-between px-4 py-2 font-medium text-sm">
                        <span>Total:</span>
                        <span>{formatPrice(calculateFormTotal(currentLineItems))}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Prompt input */}
            <div className="p-4 flex flex-col">
              <label className="block text-sm font-medium mb-2">What needs to change?</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., The part numbers are missing the prefix 'ABC-'. Please add it to all part numbers."
                className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                disabled={correctionLoading || !canRequestCorrection}
              />
              {!canRequestCorrection && (
                <p className="text-sm text-amber-600 mt-2">
                  Upload a quote document first to use the AI Assistant.
                </p>
              )}
              <div className="mt-3">
                <Action.P
                  onClick={handleSubmit}
                  disabled={correctionLoading || !prompt.trim() || !canRequestCorrection}
                  className="w-full"
                >
                  Request Corrections
                </Action.P>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full md:w-1/2 flex flex-col md:overflow-hidden">
            {!pendingCorrection && !correctionError && !correctionLoading && (
              <div className="flex items-center justify-center h-full text-gray-400 p-8 text-center">
                <p>Describe what needs to change in the left column and click Send.</p>
              </div>
            )}

            {correctionLoading && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 py-8">
                <Spinner className="h-8 w-8 mb-3" />
                <p className="font-medium">Making corrections...</p>
                <p className="text-sm text-gray-400 mt-2 max-w-xs text-center">
                  Large quotes may take 2-3 minutes to process. Please be patient.
                </p>
              </div>
            )}

            {correctionError && (
              <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {correctionError}
              </div>
            )}

            {pendingCorrection && correctedLineItems.length > 0 && (
              <>
                {/* Explanation */}
                {explanation && (
                  <div className="p-4 pb-0">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                      {explanation}
                    </div>
                  </div>
                )}

                {/* Corrected Quote table */}
                <div className="p-4 flex-shrink-0">
                  <div className="px-4 py-2 bg-blue-50 rounded-t-lg">
                    <h3 className="font-medium text-sm">Corrected Quote</h3>
                  </div>
                  <div className="border border-blue-200 rounded-b-lg overflow-hidden">
                    <div className="max-h-48 md:max-h-80 overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-blue-50 sticky top-0 border-b border-blue-200">
                          <tr>
                            <th className="text-left px-4 py-2 w-8">#</th>
                            <th className="text-left px-4 py-2">Part #</th>
                            <th className="text-left px-4 py-2">Description</th>
                            <th className="text-right px-4 py-2">Qty</th>
                            <th className="text-right px-4 py-2">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {correctedLineItems.map((li, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                              <td className="px-4 py-2 font-mono text-xs">{li.part_number}</td>
                              <td className="px-4 py-2 truncate max-w-[200px]">{li.description}</td>
                              <td className="px-4 py-2 text-right">{li.quantity}</td>
                              <td className="px-4 py-2 text-right">{formatPrice(li.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="sticky bottom-0 bg-blue-100 border-t-2 border-blue-200">
                        <div className="flex justify-between px-4 py-2 font-medium text-sm">
                          <span>Total:</span>
                          <span>{formatPrice(calculateTotal(correctedLineItems))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons - always visible at bottom */}
                <div className="mt-auto px-4 pb-4 flex justify-end items-center gap-2 flex-shrink-0">
                  <Action.S onClick={handleClose}>Cancel</Action.S>
                  <Action.S onClick={handleApplyAndContinue}>Apply & Continue</Action.S>
                  <Action.P onClick={handleApplyAndClose}>Apply & Close</Action.P>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default QuoteAssistantModal
